import React, { useState, useEffect } from 'react';
import { Modal } from "react-bootstrap";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { GetAreaByIdResponseData } from '../../../models/response/area/GetAreaByIdResponse';
import { GetAreaTypeListResponseItem } from '../../../models/response/areaType/GetAreaTypeListResponse';
import { RootState } from '../../../redux/store';
import CustomSingleSelect, { CustomSingleSelectModel } from '../../../components/CustomSingleSelect';
import { GeneralStateModel } from '../Map';
import { storeActivityIndicator } from '../../../redux/reducers/authSlice';
import { Helpers } from '../../../helpers/Helpers';
import { AreaService } from '../../../services/AreaService';
import { AddAreaResponse } from '../../../models/response/area/AddAreaResponse';
import { SketchPicker } from 'react-color';
import { ConstantService } from '../../../services/ConstantService';

interface Props {
    isShown: boolean
    onClose: () => void
    toBeUpdatedArea: GetAreaByIdResponseData | undefined
    setGeneralState: React.Dispatch<React.SetStateAction<GeneralStateModel>>
    withKml: boolean
    setWithKml: React.Dispatch<React.SetStateAction<boolean>>
}

const CreateAreaModal = ({ isShown, onClose, toBeUpdatedArea, setGeneralState, setWithKml, withKml }: Props) => {
    const dispatch = useDispatch();
    const [file, setFile] = useState<File | undefined>(undefined);
    const auth = useSelector((state: RootState) => state.auth)
    const [areaName, setAreaName] = useState('');
    const [color, setColor] = useState('#000000');
    const [isColorSelecting, setIsColorSelecting] = useState(false);
    const [isBorderColorSelecting, setIsBorderColorSelecting] = useState(false);
    const [borderColor, setBorderColor] = useState('#000000');
    const [borderWidth, setBorderWidth] = useState(1);
    const [isTransparent, setIsTransparent] = useState(false);
    const [selectedAreaType, setSelectedAreaType] = useState<CustomSingleSelectModel | undefined>(undefined);

    useEffect(() => {
        if (toBeUpdatedArea) {
            setAreaName(toBeUpdatedArea.areaName);
            setColor(toBeUpdatedArea.color);
            setBorderColor(toBeUpdatedArea.borderColor);
            setBorderWidth(toBeUpdatedArea.borderWidth);
            setIsTransparent(toBeUpdatedArea.isTransparent);
            setSelectedAreaType({ label: toBeUpdatedArea.areaTypeName, value: toBeUpdatedArea.areaTypeId })
        }
    }, [toBeUpdatedArea])

    const saveArea = async () => {
        try {
            var _generalState: any = await getCurrentHookValue(setGeneralState);

            if (Helpers.isEmpty(areaName) || Helpers.isEmpty(color) || (withKml && !file) || Helpers.isEmpty(borderColor) || Helpers.isEmpty(borderWidth) || borderWidth == 0 || !selectedAreaType) {
                toast.error('Lütfen boş alan bırakmayınız!')
                return;
            }

            dispatch(storeActivityIndicator(true))

            var response: AddAreaResponse = await AreaService.addArea({
                areaId: toBeUpdatedArea?.areaId,
                areaName: areaName,
                color: color,
                locations: withKml ? [] : _generalState.____tDrawingPolygonPoints,
                companyId: undefined,
                file: file,
                borderColor: borderColor,
                borderWidth: borderWidth,
                isTransparent: isTransparent,
                areaTypeId: selectedAreaType?.value
            });

            if (!response.isSuccess) {
                toast.error(response.message)
                dispatch(storeActivityIndicator(false))
                return;
            }

            toast.success(response.message)

            onCustomClose();
        } catch (error: any) {
            toast.error(error)
            dispatch(storeActivityIndicator(false))
        }
    }

    const onCustomClose = () => {
        setAreaName('');
        setColor('#000000');
        setBorderColor('#000000');
        setBorderWidth(1);
        setIsTransparent(false);
        setFile(undefined);
        setWithKml(false);
        setIsColorSelecting(false);
        setIsBorderColorSelecting(false);
        setSelectedAreaType(undefined)
        onClose()
    }

    async function getCurrentHookValue(setHookFunction: any) {
        return new Promise((resolve: any) => {
            setHookFunction((prev: any) => {
                resolve(prev)
                return prev;
            })
        })
    }

    return (
        <Modal
            id='kt_modal_create_app'
            tabIndex={-1}
            aria-hidden='true'
            dialogClassName='modal-dialog modal-dialog-centered mw-900px'
            show={isShown}
            onHide={onCustomClose}
            backdrop={'static'}
            keyboard={false}
        >
            <div className='modal-header'>
                <h2 className='mb-0'>Bölge Ekle</h2>
            </div>

            <div className='modal-body py-lg-10 px-lg-10'>
                <div className='row'>
                    <div className='col-md-6 mt-10'>
                        <label htmlFor="exampleFormControlInput1" className="required form-label">Bölge Adı</label>
                        <input value={areaName} onChange={(e) => setAreaName(e.target.value)} type="email" className="form-control form-control-solid" />
                    </div>

                    <div className='col-md-6 mt-10'>
                        <label htmlFor="exampleFormControlInput1" className="required form-label">Bölge Tipi</label>

                        <CustomSingleSelect
                            selectedValue={selectedAreaType}
                            onChange={setSelectedAreaType}
                            onMenuOpen={() => ConstantService.getConstantList('AREA_TYPE')}
                            labelName='value'
                            valueName='constantId'
                            insertable={{
                                onSubmit: { code: 'AREA_TYPE' }
                            }}
                        />
                    </div>

                    <div className='col-md-6 mt-10'>
                        <label htmlFor="exampleFormControlInput1" className="required form-label">Dolgu Rengi</label>

                        <div className='d-flex'>
                            <div onClick={() => setIsColorSelecting(!isColorSelecting)} className='d-flex form-control form-control-solid' style={{ width: 100, background: color }}>
                                &nbsp;
                            </div>

                            <div style={{ marginLeft: 15 }} className="form-check form-check-custom form-check-solid d-flex justify-content-center">
                                <input onChange={(e) => { setIsColorSelecting(false); setIsBorderColorSelecting(false); setIsTransparent(!isTransparent); }} className="form-check-input" type="checkbox" value="" id={"areaAddCheckboxIsTransparent"} checked={isTransparent} />
                                <label className="form-check-label" htmlFor={"areaAddCheckboxIsTransparent"}>
                                    Transparan
                                </label>
                            </div>
                        </div>

                        {
                            isColorSelecting && (
                                <div style={{ position: 'absolute' }}>
                                    <SketchPicker color={color} onChange={(e) => setColor(e.hex)} />
                                </div>
                            )
                        }
                    </div>

                    <div className='col-md-6 mt-10'>
                        <div className='row'>
                            <div className='col-md-6'>
                                <label htmlFor="exampleFormControlInput1" className="required form-label">Kenarlık Rengi</label>

                                <div onClick={() => setIsBorderColorSelecting(!isBorderColorSelecting)} className='form-control form-control-solid' style={{ width: 100, background: borderColor }}>
                                    &nbsp;
                                </div>

                                {
                                    isBorderColorSelecting && (
                                        <div style={{ position: 'absolute' }}>
                                            <SketchPicker color={borderColor} onChange={(e) => setBorderColor(e.hex)} />
                                        </div>
                                    )
                                }
                            </div>
                            <div className='col-md-6'>
                                <label htmlFor="exampleFormControlInput1" className="required form-label">Kenarlık Kalınlığı</label>
                                <input value={borderWidth} onChange={(e) => setBorderWidth(parseInt(e.target.value))} type="number" className="form-control form-control-solid" />
                            </div>
                        </div>
                    </div>


                    {

                        withKml && (
                            <div className="col-md-6 mt-10">
                                <label htmlFor="exampleFormControlInput1" className="required form-label">KML Dosyası</label>
                                <input onChange={(e) => setFile(e && e.target && e.target.files && e.target.files.length > 0 ? e.target.files[0] : undefined)} accept=".kml" type="file" className="form-control form-control-solid" />
                            </div>
                        )
                    }
                </div>
            </div>

            <div className="modal-footer">
                <button
                    onClick={onCustomClose}
                    type="button"
                    className="btn btn-danger"
                    data-bs-dismiss="modal"
                >
                    Kapat
                </button>
                <button onClick={saveArea} type="button" className="btn btn-primary">
                    Kaydet
                </button>
            </div>
        </Modal >
    )
}

export default CreateAreaModal;